import React, { useEffect, useState } from "react";
import AdminShell from "@/components/AdminShell";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { adminAuth } from "@/lib/api";
import { Plus, Pencil, Trash2, Star, Eye, EyeOff, Loader2, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/university/alumni`;
const auth = () => ({ Authorization: `Bearer ${adminAuth.getToken()}` });
const blank = {
  student_email: "", student_name: "", headshot_url: "", bio: "", business_name: "",
  website: "", location: "", graduation_year: 0, specialties: [], credentials_held: [],
  public_visible: true, featured: false, status: "active",
};

export default function AdminUniversityAlumni() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/list`, { headers: auth() });
      setItems(r.data?.items || []);
    } catch (e) {
      if (e?.response?.status === 401) navigate("/admin/login?next=/admin/university/alumni");
      else toast.error("Load failed");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const startNew = () => { setForm({ ...blank }); setEditing("new"); };
  const startEdit = (a) => {
    setForm({
      ...blank, ...a,
      specialties: a.specialties || [], credentials_held: a.credentials_held || [],
      featured: !!a.featured, public_visible: a.public_visible !== false,
    });
    setEditing(a.student_email);
  };
  const save = async () => {
    setSaving(true);
    try {
      if (editing === "new") {
        await axios.post(`${API}/admin/profiles`, form, { headers: auth() });
        toast.success("Alumni created");
      } else {
        const { student_email, ...rest } = form;
        await axios.patch(`${API}/admin/profiles/${editing}`, rest, { headers: auth() });
        toast.success("Alumni updated");
      }
      setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };
  const del = async (email) => {
    if (!confirm(`Delete alumni profile for ${email}?`)) return;
    try {
      await axios.delete(`${API}/admin/profiles/${email}`, { headers: auth() });
      toast.success("Deleted"); load();
    } catch (e) { toast.error("Delete failed"); }
  };
  const toggle = async (email, field, value) => {
    try {
      await axios.patch(`${API}/admin/profiles/${email}`, { [field]: value }, { headers: auth() });
      load();
    } catch (e) { toast.error(`Toggle ${field} failed`); }
  };

  return (
    <AdminShell door="university" title="Alumni Directory">
      <div data-testid="admin-alumni-page">
        <div className="flex items-center justify-between mb-4">
          <div className="text-sm text-slate-600">{items.length} alumni · live directory at <code className="text-xs">/university/alumni</code></div>
          <button onClick={startNew} className="rounded-xl bg-purple-900 text-white h-9 px-3 text-sm inline-flex items-center gap-1.5" data-testid="alumni-add">
            <Plus className="w-4 h-4" />Add Alumni
          </button>
        </div>

        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (
          <div className="rounded-2xl bg-white border border-slate-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr><th className="text-left px-4 py-2">Name</th><th className="text-left px-4 py-2">Email</th>
                <th className="text-left px-4 py-2">Year</th><th className="text-left px-4 py-2">Status</th>
                <th className="text-left px-4 py-2">Featured</th><th className="text-left px-4 py-2">Public</th>
                <th className="text-right px-4 py-2">Actions</th></tr>
              </thead>
              <tbody>
                {items.length === 0 && <tr><td colSpan={7} className="px-4 py-6 text-center text-slate-400">No alumni profiles yet.</td></tr>}
                {items.map((a) => (
                  <tr key={a.id} className="border-t border-slate-100" data-testid={`alumni-row-${a.student_email}`}>
                    <td className="px-4 py-2 font-medium">{a.student_name || "—"}</td>
                    <td className="px-4 py-2 text-slate-600">{a.student_email}</td>
                    <td className="px-4 py-2">{a.graduation_year || "—"}</td>
                    <td className="px-4 py-2 text-xs">{a.status}</td>
                    <td className="px-4 py-2"><button onClick={() => toggle(a.student_email, "featured", !a.featured)} className={a.featured ? "text-amber-600" : "text-slate-300"}><Star className="w-4 h-4" fill={a.featured ? "currentColor" : "none"} /></button></td>
                    <td className="px-4 py-2"><button onClick={() => toggle(a.student_email, "public_visible", a.public_visible === false)} className={a.public_visible !== false ? "text-emerald-600" : "text-slate-300"}>{a.public_visible !== false ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}</button></td>
                    <td className="px-4 py-2 text-right">
                      <button onClick={() => startEdit(a)} className="text-purple-900 mr-3" data-testid={`alumni-edit-${a.student_email}`}><Pencil className="w-4 h-4" /></button>
                      <button onClick={() => del(a.student_email)} className="text-rose-600" data-testid={`alumni-del-${a.student_email}`}><Trash2 className="w-4 h-4" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Edit drawer */}
        {editing && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setEditing(null)}>
            <div className="bg-white rounded-2xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="alumni-form">
              <div className="flex items-center justify-between mb-4">
                <div className="font-display text-xl">{editing === "new" ? "Add Alumni" : `Edit ${form.student_name || form.student_email}`}</div>
                <button onClick={() => setEditing(null)}><X className="w-4 h-4" /></button>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <label className="col-span-2"><span className="text-xs text-slate-600">Email *</span>
                  <input disabled={editing !== "new"} value={form.student_email} onChange={(e) => setForm({ ...form, student_email: e.target.value })} className="w-full border rounded-lg h-9 px-2 mt-0.5 disabled:bg-slate-100" data-testid="alumni-email" /></label>
                <label><span className="text-xs text-slate-600">Name</span>
                  <input value={form.student_name} onChange={(e) => setForm({ ...form, student_name: e.target.value })} className="w-full border rounded-lg h-9 px-2 mt-0.5" data-testid="alumni-name" /></label>
                <label><span className="text-xs text-slate-600">Graduation Year</span>
                  <input type="number" value={form.graduation_year} onChange={(e) => setForm({ ...form, graduation_year: Number(e.target.value) })} className="w-full border rounded-lg h-9 px-2 mt-0.5" /></label>
                <label className="col-span-2"><span className="text-xs text-slate-600">Headshot URL</span>
                  <input value={form.headshot_url} onChange={(e) => setForm({ ...form, headshot_url: e.target.value })} placeholder="https://..." className="w-full border rounded-lg h-9 px-2 mt-0.5" data-testid="alumni-photo" /></label>
                <label className="col-span-2"><span className="text-xs text-slate-600">Bio</span>
                  <textarea value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} rows={3} className="w-full border rounded-lg p-2 mt-0.5" /></label>
                <label><span className="text-xs text-slate-600">Business Name</span>
                  <input value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} className="w-full border rounded-lg h-9 px-2 mt-0.5" /></label>
                <label><span className="text-xs text-slate-600">Website</span>
                  <input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} className="w-full border rounded-lg h-9 px-2 mt-0.5" /></label>
                <label className="col-span-2"><span className="text-xs text-slate-600">Credentials (comma-separated)</span>
                  <input value={(form.credentials_held || []).join(", ")} onChange={(e) => setForm({ ...form, credentials_held: e.target.value.split(",").map(s => s.trim()).filter(Boolean) })} className="w-full border rounded-lg h-9 px-2 mt-0.5" /></label>
                <label><span className="text-xs text-slate-600">Membership Status</span>
                  <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="w-full border rounded-lg h-9 px-2 mt-0.5">
                    {["active", "dues_due", "lapsed", "lifetime", "suspended"].map(s => <option key={s}>{s}</option>)}
                  </select></label>
                <div className="col-span-2 flex items-center gap-4 mt-2">
                  <label className="flex items-center gap-2"><input type="checkbox" checked={!!form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} />Featured</label>
                  <label className="flex items-center gap-2"><input type="checkbox" checked={form.public_visible !== false} onChange={(e) => setForm({ ...form, public_visible: e.target.checked })} />Public Directory</label>
                </div>
              </div>
              <div className="mt-6 flex justify-end gap-2">
                <button onClick={() => setEditing(null)} className="rounded-xl border h-9 px-3 text-sm">Cancel</button>
                <button onClick={save} disabled={saving || !form.student_email} className="rounded-xl bg-purple-900 text-white h-9 px-4 text-sm disabled:opacity-50" data-testid="alumni-save">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminShell>
  );
}
